import { useLayoutEffect, useRef } from 'react';
import { FormTemplate } from '../../types/schema';
import {
    buildFormsTemplateDropdownOptions,
    type FormsTemplateScope,
} from './formsTemplateDropdownUtils';

/** Size the menu to the viewport below its top edge. */
function useFillViewportBelow<T extends HTMLElement>() {
    const ref = useRef<T>(null);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const visualViewport = window.visualViewport;

        const fit = () => {
            const viewportHeight = visualViewport?.height ?? window.innerHeight;
            const offsetTop = visualViewport?.offsetTop ?? 0;
            const top = Math.max(0, el.getBoundingClientRect().top - offsetTop);
            el.style.setProperty('--forms-template-dropdown-vvh', `${viewportHeight}px`);
            el.style.setProperty('--forms-template-dropdown-top', `${top}px`);
        };

        fit();
        window.addEventListener('resize', fit);
        window.addEventListener('scroll', fit, true);
        visualViewport?.addEventListener('resize', fit);
        visualViewport?.addEventListener('scroll', fit);
        return () => {
            window.removeEventListener('resize', fit);
            window.removeEventListener('scroll', fit, true);
            visualViewport?.removeEventListener('resize', fit);
            visualViewport?.removeEventListener('scroll', fit);
        };
    }, []);

    return ref;
}

interface FormsTemplateDropdownProps {
    formTemplates: FormTemplate[];
    scope: FormsTemplateScope;
    companyForms?: Array<{ id?: string | null; name?: string | null }>;
    onSelectLibraryTemplate: (templateId: string) => void;
    onSelectCompanyForm: (formId: string) => void;
}

export const FormsTemplateDropdown = ({
    formTemplates,
    scope,
    companyForms = [],
    onSelectLibraryTemplate,
    onSelectCompanyForm,
}: FormsTemplateDropdownProps) => {
    const menuRef = useFillViewportBelow<HTMLDivElement>();
    const { libraryOptions, companyOptions, sectionLabel, companySectionLabel } =
        buildFormsTemplateDropdownOptions({
            formTemplates,
            scope,
            companyForms,
        });

    if (libraryOptions.length === 0 && companyOptions.length === 0) {
        return (
            <div ref={menuRef} className="forms-template-dropdown" role="listbox" aria-label="Start from template">
                <div className="forms-template-dropdown__empty">No templates available for this tab.</div>
            </div>
        );
    }

    return (
        <div ref={menuRef} className="forms-template-dropdown" role="listbox" aria-label="Start from template">
            {companyOptions.length > 0 && (
                <>
                    <div className="forms-template-dropdown__section-label">{companySectionLabel}</div>
                    {companyOptions.map((option) => (
                        <button
                            key={`company-${option.id}`}
                            type="button"
                            role="option"
                            className="forms-template-dropdown__option"
                            onClick={() => onSelectCompanyForm(option.id)}
                        >
                            {option.name}
                        </button>
                    ))}
                </>
            )}
            {libraryOptions.length > 0 && (
                <>
                    <div
                        className={[
                            'forms-template-dropdown__section-label',
                            companyOptions.length > 0 ? 'forms-template-dropdown__section-label--bordered' : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                    >
                        {sectionLabel}
                    </div>
                    {libraryOptions.map((option) => (
                        <button
                            key={`library-${option.id}`}
                            type="button"
                            role="option"
                            className="forms-template-dropdown__option"
                            onClick={() => onSelectLibraryTemplate(option.id)}
                        >
                            {option.name}
                        </button>
                    ))}
                </>
            )}
        </div>
    );
};
